import { storeToRefs } from 'pinia';
import { useLandingStore } from '@/store/landingStore';
import { useVisitorCountry } from '@/composables/queries/useVisitorCountry';
import { countryFromBrowser } from '@/utils/landing/detectCountry';
import { getCountryProfile, hasCountryProfile, type CountryProfile } from '@/utils/landing/countryProfiles';
import { priceContext, type PriceContext } from '@/utils/landing/landingPricing';
import { DemoCountrySource } from '@/types/composition-functions';

export interface ResolvedCountry {
  /** ISO alpha-2 code, or null when nothing points to a country. */
  code: string | null;
  source: DemoCountrySource | null;
}

/** The visitor's pick wins, then the CDN's IP country, then browser hints. */
export function resolveCountry(
  override: string | null,
  ipCountry: string | null,
  browserCountry: string | null,
): ResolvedCountry {
  if (override) return { code: override, source: DemoCountrySource.Manual };
  if (ipCountry) return { code: ipCountry, source: DemoCountrySource.Ip };
  if (browserCountry) return { code: browserCountry, source: DemoCountrySource.Browser };
  return { code: null, source: null };
}

/**
 * Which country the landing page is tailored to, and the profile and price
 * currency that follow from it. Shared by every landing component.
 */
export function useLandingCountry() {
  const store = useLandingStore();
  const { countryOverride } = storeToRefs(store);
  const { countryCode: ipCountry } = useVisitorCountry();
  const browserCountry = countryFromBrowser();

  const resolved = computed(() => resolveCountry(countryOverride.value, ipCountry.value, browserCountry));
  const profile = computed<CountryProfile>(() => getCountryProfile(resolved.value.code));
  const prices = computed<PriceContext>(() => priceContext(profile.value));
  const isProfiled = computed(() => hasCountryProfile(resolved.value.code));

  return {
    countryCode: computed(() => resolved.value.code),
    countrySource: computed(() => resolved.value.source),
    profile,
    prices,
    isProfiled,
    setCountry: store.setCountry,
  };
}
